import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, DemoBadge } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Users, ShieldCheck, TrendingUp } from "lucide-react";

const ADVISOR_IMG = "https://images.unsplash.com/photo-1765648684555-de2d0f6af467?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxzbWlsaW5nJTIwcHJvZmVzc2lvbmFsJTIwZGVza3xlbnwwfHx8fDE3ODgxNDUwMjV8MA&ixlib=rb-4.1.0&q=85";

export default function EmployerConsole() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/employer/overview")
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Unable to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 sm:p-12 max-w-6xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;
  if (error) return <div className="p-12 max-w-6xl mx-auto text-center text-muted-foreground" data-testid="employer-error">{error}</div>;

  const { employer, roster } = data;
  const stats = [
    { label: "Employees", value: employer.employee_count, icon: Users },
    { label: "Enrolled", value: employer.enrolled_count, icon: ShieldCheck },
    { label: "Completion", value: `${employer.completion_pct}%`, icon: TrendingUp },
  ];

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-6xl mx-auto animate-rise">
      <div className="relative rounded-2xl overflow-hidden mb-8 bg-navy-800">
        <img src={ADVISOR_IMG} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-20" />
        <div className="relative p-8">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-gold text-xs tracking-[0.2em] uppercase font-bold">Employer console</span>
            <DemoBadge />
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-white tracking-tight">{employer.name}</h1>
          <p className="text-mist-200 mt-2">Group {employer.group_number} · {employer.industry} · {employer.state}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl border border-mist-200 p-6" data-testid={`employer-stat-${s.label.toLowerCase()}`}>
              <div className="w-11 h-11 rounded-lg bg-mist-100 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-navy-800" /></div>
              <div className="font-display text-4xl font-bold text-navy-900">{s.value}</div>
              <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-mist-200 p-6 mb-8">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-semibold text-navy-900">Open enrollment completion</span>
          <span className="text-gold-dark font-bold">{employer.completion_pct}%</span>
        </div>
        <Progress value={employer.completion_pct} className="h-2" />
      </div>

      <div className="bg-white rounded-xl border border-mist-200 overflow-hidden">
        <div className="p-6 border-b border-mist-200"><h3 className="font-display text-xl font-bold text-navy-900">Employee roster</h3></div>
        <Table data-testid="employer-roster">
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Household</TableHead>
              <TableHead>Active benefits</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roster.length ? roster.map((r) => (
              <TableRow key={r.id} data-testid={`roster-${r.id}`}>
                <TableCell>
                  <p className="font-semibold text-navy-900">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.email}</p>
                </TableCell>
                <TableCell>{r.household_members} member{r.household_members === 1 ? "" : "s"}</TableCell>
                <TableCell>{r.active_benefits}</TableCell>
                <TableCell>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${r.status === "Enrolled" ? "bg-green-100 text-green-700" : "bg-mist-200 text-navy-700"}`}>{r.status}</span>
                </TableCell>
              </TableRow>
            )) : (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No employees yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
